// Time boundaries for greetings
const GREETING_HOURS = {
  MORNING_START: 5,
  AFTERNOON_START: 12,
  EVENING_START: 17,
  NIGHT_START: 21,
} as const;

/**
 * Get time-based greeting in Hebrew
 * Returns appropriate greeting based on current time of day
 */
export const getTimeBasedGreeting = (): string => {
  const hour = new Date().getHours();

  if (hour >= GREETING_HOURS.MORNING_START && hour < GREETING_HOURS.AFTERNOON_START) {
    return "בוקר טוב";
  } else if (hour >= GREETING_HOURS.AFTERNOON_START && hour < GREETING_HOURS.EVENING_START) {
    return "צהריים טובים";
  } else if (hour >= GREETING_HOURS.EVENING_START && hour < GREETING_HOURS.NIGHT_START) {
    return "ערב טוב";
  } else {
    return "לילה טוב";
  }
};

/**
 * Get encouraging message based on business state
 */
export const getContextualMessage = (context: {
  hasServices: boolean;
  hasCustomers: boolean;
  hasUpcomingSlots: boolean;
  businessName?: string;
}): string => {
  const { hasServices, hasCustomers, hasUpcomingSlots, businessName } = context;

  // First time user - no services yet
  if (!hasServices) {
    return businessName
      ? `ברוכים הבאים! ${businessName} - בואו נתחיל להגדיר את השירותים שלכם.`
      : "ברוכים הבאים! בואו נתחיל להגדיר את השירותים שלכם.";
  }

  // Has services but no customers yet
  if (hasServices && !hasCustomers) {
    return "מעולה! השירותים שלכם מוכנים. עכשיו בואו נשתף את הקישור עם לקוחות.";
  }

  // Has customers but no upcoming slots
  if (hasCustomers && !hasUpcomingSlots) {
    return "נראה שאין תורים מתוכננים. הלקוחות שלכם ממתינים לתורים חדשים!";
  }

  // All good - has everything
  if (hasServices && hasCustomers && hasUpcomingSlots) {
    const messages = [
      "הכל נראה מצוין! העסק שלכם פועל בשיא הכוח.",
      "יום פרודוקטיבי! יש לכם תורים מתוכננים ולקוחות ממתינים.",
      "מעולה! העסק ממשיך לצמוח.",
    ];
    // Rotate through messages based on day of week
    const dayIndex = new Date().getDay() % messages.length;
    return messages[dayIndex];
  }

  return "שמחים לראות אתכם!";
};

/**
 * Get personalized greeting combining time and context
 */
export const getPersonalizedGreeting = (ownerName?: string): string => {
  const timeGreeting = getTimeBasedGreeting();

  if (ownerName) {
    return `${timeGreeting}, ${ownerName}!`;
  }

  return `${timeGreeting}!`;
};
